'use client';
import { FC } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { twMerge } from 'tailwind-merge';
import dayjs from 'dayjs';

import { Button, Heading, Svg, Text } from '@/components';
import { IMAGE_URI } from '@/config/endpoints';
import { getKeyValue } from '@/utils/helper';
import { CalendarIcon, LocationPinIcon } from '@/icons';
import { IMAGE_BLUR_DATA_URL } from '@/constants';

interface PropTypes {
  item: any;
  classNames?: { root?: string };
}

const WorkshopsCard: FC<PropTypes> = ({ item, classNames }) => {
  const router = useRouter();
  const customAttributes = getKeyValue(
    item?.custom_attributes,
    'attribute_code'
  );
  const image = customAttributes?.thumbnail;
  const location = customAttributes?.event_location;
  const eventStartDate = customAttributes?.event_start_date;

  return (
    <div
      className={twMerge(
        'group col-span-1 row-span-1 flex w-full max-w-76.5 flex-1 flex-col gap-6 md:gap-9',
        classNames?.root
      )}>
      <Link
        href={`/kitchen-studio?tag=events-list`}
        passHref
        className='block space-y-2'>
        <div
          className={twMerge(
            'relative flex items-center justify-center rounded-lg bg-[#fef9f8] p-0'
          )}>
          {image && (
            <div className={twMerge('relative aspect-square w-full')}>
              <Image
                alt={item?.name}
                title={item?.name}
                src={`${process.env.NEXT_PUBLIC_CDN_LINK}${IMAGE_URI}${image}`}
                fill
                sizes='40vw'
                placeholder='blur'
                style={{ objectFit: 'cover' }}
                blurDataURL={IMAGE_BLUR_DATA_URL}
                className='rounded-lg'
              />
            </div>
          )}
        </div>
        <div
          className={twMerge('h-36 space-y-2 md:h-32 lg:h-32')}
          aria-label='title-price'>
          <Heading
            className={twMerge(
              'line-clamp-2 px-0 text-left text-lg font-semibold leading-5.5 text-black'
            )}>
            {item?.name}
          </Heading>
          <div className='align-center flex justify-between'>
            <Text
              className={twMerge(
                'line-clamp-1 px-0 text-left text-lg font-semibold'
              )}>
              {customAttributes?.special_price ? (
                <>
                  <span>₹{customAttributes.special_price}</span>
                  <span className='ml-3 font-light text-gray-400 line-through'>
                    ₹{item?.price}
                  </span>
                </>
              ) : (
                <span>₹{item?.price}</span>
              )}
            </Text>
          </div>
          <div className='flex flex-col gap-y-1'>
            <div className='flex w-fit items-start justify-start gap-1 rounded-2xl md:items-center'>
              <div className='h-4 w-4 max-md:pt-1'>
                <Svg className='text-[#262626]' viewBox='0 0 28 28'>
                  <CalendarIcon color='#262626' opacity='1' />
                </Svg>
              </div>
              <Text className='line-clamp-1 px-0 text-sm text-[#262626]'>
                {dayjs(eventStartDate).format('DD MMMM YYYY')}
              </Text>
            </div>
            <div className='flex w-fit items-start justify-start gap-1 rounded-2xl py-1 md:items-center'>
              <div className='h-4 w-4 max-md:pt-1'>
                <Svg className='stroke-[#262626]' viewBox='0 0 24 24'>
                  <LocationPinIcon />
                </Svg>
              </div>
              <Text className='line-clamp-2 px-0 text-sm text-[#262626]'>
                {location}
              </Text>
            </div>
          </div>
        </div>
      </Link>
      <div
        className={twMerge(
          'visible flex items-center justify-start gap-y-1 lg:invisible lg:group-hover:visible'
        )}>
        <Button
          className='py-[0.9rem] text-sm font-bold disabled:opacity-100'
          label='Attend This Workshop'
          onClick={() => router.push('/kitchen-studio?tag=events-list')}
        />
      </div>
    </div>
  );
};

export default WorkshopsCard;
